import React, { useState } from "react";

import { IconComponents, IconMetrics, IconTrace } from "~/components/icon";
import { cx } from "~/tools/class-variance";
import type { Record } from "~/tools/record";

import type { BorgActionType } from "../models/borg-types";

import type { ActionPartType } from "./_part-types";
import type { ComponentViewMode } from "./_use-navigate-to-component";

const ACTION_MAX_VISIBLE = 7;

const ACTION_TYPE_ICONS = {
	view_component: IconComponents,
	view_logs: IconComponents,
	view_metrics: IconMetrics,
	view_traces: IconTrace,
} satisfies Record<BorgActionType, React.FC<{ className?: string }>>;

const ACTION_TYPE_TO_MODE = {
	view_component: "highlights",
	view_logs: "highlights",
	view_metrics: "metrics",
	view_traces: "traces",
} satisfies Record<BorgActionType, ComponentViewMode>;

export const ActionGroup: React.FC<{
	parts: ReadonlyArray<ActionPartType>;
	onNavigateToComponent?: (identifier: string, mode?: ComponentViewMode) => void;
}> = ({ parts, onNavigateToComponent }) => {
	const [showAll, setShowAll] = useState(false);
	const visible = showAll ? parts : parts.slice(0, ACTION_MAX_VISIBLE);
	const overflowCount = parts.length - ACTION_MAX_VISIBLE;

	return (
		<div className="flex flex-col gap-1.5">
			<span className="font-semibold text-10 text-gray-500 uppercase tracking-wider">Actions</span>
			<div className="flex flex-wrap gap-1.5">
				{visible.map(({ action_type: actionType, label, part_id: partId, urn }, index) => {
					const Icon = (actionType && ACTION_TYPE_ICONS[actionType]) ?? IconComponents;
					const identifier = urn;
					const mode = actionType ? ACTION_TYPE_TO_MODE[actionType] : undefined;
					const canNavigate = identifier != null && onNavigateToComponent != null;

					return (
						<button
							className={cx(
								"inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs shadow-sm transition-colors",
								canNavigate
									? "cursor-pointer border-gray-400 bg-white text-gray-800 hover:border-primary-500 hover:text-primary-500"
									: "cursor-default border-gray-300 bg-gray-100 text-gray-500",
							)}
							disabled={!canNavigate}
							key={partId ?? `action:${urn ?? label ?? ""}:${index}`}
							onClick={() => canNavigate && onNavigateToComponent(identifier, mode)}
							type="button"
						>
							<Icon className="size-3" />
							{label}
						</button>
					);
				})}
			</div>
			{overflowCount > 0 && !showAll && (
				<button
					className="cursor-pointer text-left text-gray-500 text-xs transition-colors hover:text-primary-600"
					onClick={() => setShowAll(true)}
					type="button"
				>
					and {overflowCount} more...
				</button>
			)}
		</div>
	);
};
